/**
 * MVP-490. A room-based venue puts a PC with a keyboard and a mouse in every room, and the customer
 * sitting at it should be able to start searching from wherever they are instead of hunting for the
 * search box. `/` because the web has already trained people on it, it is one key, and — the part
 * that matters here — it is not a digit, so it cannot collide with `GuestCodePad`'s document-level
 * digit capture (MVP-274 R1: the code printed next to every song in the venue's book).
 *
 * The decision lives here rather than in the component so it can be tested as arithmetic, the same
 * split `guestCode.ts` makes for the pad. No DOM types: the caller reads the three live-DOM facts
 * this needs and passes them in.
 */

/** The parts of a keydown this decision reads. Shift is deliberately absent — see below. */
export interface GuestSearchHotkeyEvent {
  key: string;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

/** What the page looks like at the moment the key arrived. */
export interface GuestSearchHotkeyContext {
  /** Focus is somewhere that owns the keys — an `<input>`, a `<textarea>`, a `<select>` or a
   * contenteditable. A singer name containing a slash must still type. Callers read this with
   * `lib/isTypingTarget.ts` (MVP-496), which is also what the operator hotkeys use. */
  isTypingTarget: boolean;
  /** A `[role="dialog"]` is up (the request sheet opens over both doors). Same live-DOM idiom the
   * pad uses at `GuestCodePad.tsx:34`, and the same reason: a key pressed behind the sheet would
   * silently rewrite the screen the customer comes back to. */
  isDialogOpen: boolean;
}

/**
 * True when this keydown should take the guest to the search field.
 *
 * Matched on `key`, not `code`, and WITHOUT consulting Shift. Both for the same reason
 * `features/ops/hotkeys.ts`'s `ignoresShift` exists: a single printable non-letter character says
 * nothing reliable about which physical key produced it or whether Shift was held. On a US layout
 * `/` is unshifted; on a Thai Kedmanee layout it sits unshifted on a different physical key
 * entirely. `key` is the character the guest actually typed, which is the only thing this hotkey is
 * about — demanding `shiftKey === false` (or a particular `code`) would make the shortcut dead on
 * the layout most of this venue's customers use.
 *
 * Ctrl/Alt/Meta ARE consulted: those are real chords owned by the browser and the OS.
 */
export function shouldFocusGuestSearch(ev: GuestSearchHotkeyEvent, ctx: GuestSearchHotkeyContext): boolean {
  if (ev.key !== '/') return false;
  if (ev.ctrlKey || ev.altKey || ev.metaKey) return false;
  if (ctx.isTypingTarget) return false;
  if (ctx.isDialogOpen) return false;
  return true;
}
